// dialogStore.ts
import { create } from 'zustand';
import React from 'react';

interface DialogContentProps {
    onClose: () => void;
    data: any;
    onSave: (data: any) => void;
}

type DialogContentComponent = React.ComponentType<DialogContentProps>;

interface DialogStore {
    isOpen: boolean;
    activeDialog: { type: string; data: any } | null;
    registeredDialogs: Record<string, DialogContentComponent>;
    onSave: (data: any) => void;
    openDialog: (type: string, data: any, onSave?: (data:any) => void) => void;
    closeDialog: () => void;
    registerDialog: (type: string, component: DialogContentComponent) => void;
    handleSave: (data: any) => void;
    // Compatibility with old API
    dialogKey?: string | null;
    dialogProps?: Record<string, any>;
}

export const useDialogStore = create<DialogStore>((set, get) => ({
    isOpen: false,
    activeDialog: null,
    onSave: (data: any) => {
        console.log("Default onSave handler called with data:", data);
    },
    registeredDialogs: {},
    openDialog: (type, data, onSave) => {
        // Ensure type is valid
        if (!type) {
            console.error('Dialog type is required');
            return;
        }
        
        // Check if dialog is registered
        const { registeredDialogs } = get();
        if (!registeredDialogs[type]) {
            console.error(`Dialog component with key "${type}" not found. Available dialogs: ${Object.keys(registeredDialogs).join(', ')}`);
            return;
        }
        
        // Ensure data is not null or undefined
        const safeData = data || {};
        
        // First close any existing dialog
        set({
            isOpen: false,
            activeDialog: null
        });
        
        // Then open the new dialog in the next tick to avoid React batching issues
        setTimeout(() => {
            // Update the onSave function if provided
            if (typeof onSave === 'function') {
                set({ 
                    isOpen: true, 
                    activeDialog: { type, data: safeData },
                    onSave: onSave
                });
                console.log("Dialog opened with custom onSave handler");
            } else {
                // Use default onSave
                set({ 
                    isOpen: true, 
                    activeDialog: { type, data: safeData }
                });
                console.log("Dialog opened with default onSave handler");
            }
        }, 0);
    },
    closeDialog: () => {
        // Always reset both simultaneously - this keeps things simple
        set({ 
            isOpen: false, 
            activeDialog: null,
            // Reset onSave to default
            onSave: (data: any) => {
                console.log("Default onSave handler called with data:", data);
            }
        });
        console.log("Dialog closed");
    },
    registerDialog: (type, component) =>
        set(state => ({
            registeredDialogs: { ...state.registeredDialogs, [type]: component }
        })),
    handleSave: (data) => {
        console.log('Dialog handleSave called with data:', data);
        const { onSave } = get();
        
        // First close the dialog
        get().closeDialog();
        
        // Then call onSave in the next tick if available
        if (typeof onSave === 'function') {
            setTimeout(() => {
                try {
                    onSave(data);
                } catch (error) {
                    console.error('Error in dialog onSave handler:', error);
                }
            }, 0);
        } else {
            console.warn('No onSave function provided for dialog');
        }
    },
}));

// Export a custom hook that provides the dialog functionality
export const useDialog = () => {
    const { 
        openDialog: storeOpenDialog, 
        closeDialog, 
        registerDialog, 
        handleSave,
        isOpen,
        activeDialog
    } = useDialogStore();

    // Wrapper for openDialog to make it easier to use
    const openDialog = (type: string, data: any = {}, onSave?: (data: any) => void) => {
        // Ensure data is not null or undefined
        const safeData = data || {};
        
        // Log for debugging
        console.log(`Opening dialog: ${type}`, { data: safeData, hasOnSave: typeof onSave === 'function' });
        
        // Call the store's openDialog function with safe data
        storeOpenDialog(type, safeData, onSave);
    };

    return {
        openDialog,
        closeDialog,
        registerDialog,
        handleSave,
        isOpen,
        activeDialog
    };
};